"use client";

import { createContext, useContext, type ReactNode } from "react";
import type { Product } from "@/types";

/**
 * Server-rendered first page of the product detail carousels
 * (SimilarCategoryProducts / PartnerProducts). Provided by the product page
 * around ProductDetailClient, so the carousels render real cards — and links —
 * in the ISR HTML instead of a skeleton. Absent → they fetch on mount as before.
 */
interface RelatedProductsValue {
  productId: string;
  similar?: Product[];
  partner?: Product[];
}

const RelatedProductsContext = createContext<RelatedProductsValue | null>(null);

export function RelatedProductsProvider({
  value,
  children,
}: {
  value: RelatedProductsValue;
  children: ReactNode;
}) {
  return (
    <RelatedProductsContext.Provider value={value}>{children}</RelatedProductsContext.Provider>
  );
}

/**
 * Initial products for a carousel, only when the server data was computed for
 * the same exclusion (the current product) — otherwise undefined.
 */
export function useInitialRelatedProducts(
  kind: "similar" | "partner",
  excludeProductIds: string[],
): Product[] | undefined {
  const value = useContext(RelatedProductsContext);
  if (!value) return undefined;
  if (excludeProductIds.length !== 1 || excludeProductIds[0] !== value.productId) {
    return undefined;
  }
  return value[kind];
}
